import React, { useEffect, useState } from 'react';
import { Calculator, Clock, Fingerprint, Landmark, MapPin, Plus, Save, SlidersHorizontal, Timer, Trash2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import { Select } from '../components/Controls';
import { fmtMoney } from '../format';

// Time & Attendance > T&A Settings: the parameters Stephanet keeps under Settings > Payroll Settings that time and attendance works
// from — System Parameters (working day, hours, grace, timesheet weekend rates, monetization tax), OT Rates, the statutory rates and
// PAYE Table (the Attendance Report's payroll preview). Project sites and their outstation allowances are kept in User Access
// Control > Org Hierarchy. Only a super admin changes them.

const PARAMS = [
  ['Working Day', Clock, [
    ['default_shift_start', 'Default Shift Start', 'time'], ['default_shift_end', 'Default Shift End', 'time'],
    ['grace_minutes', 'Grace Period (minutes)', 'int'], ['break_minutes', 'Break (minutes)', 'int'], ['break_after_hours', 'Break After (hours)', 'num'],
    ['hours_per_day', 'Hours per Day', 'num'], ['working_days_per_month', 'Working Days per Month', 'int'],
    ['ot_requires_authorization', 'Overtime Needs Authorization', 'bool'],
  ]],
  ['Timesheet & Leave', Calculator, [
    ['ts_saturday_rate', 'Saturday Rate (× daily rate)', 'num'], ['ts_sunday_rate', 'Sunday / Holiday Rate (× daily rate)', 'num'],
    ['leave_monetization_tax_rate', 'Leave Monetization Tax (%)', 'num'],
  ]],
  ['Biometric Punches', Fingerprint, [
    ['bio_day_start_offset_minutes', 'Work Day Opens Before Shift (minutes)', 'int'], ['bio_min_gap_minutes', 'Double-Tap Gap (minutes)', 'int'],
    ['bio_future_tolerance_minutes', 'Hold Punches Dated Ahead By (minutes)', 'int'], ['bio_backlog_days', 'Hold Punches Older Than (days)', 'int'],
  ]],
];
const OT = [['weekday', 'Weekday'], ['weekend', 'Sunday / Weekend'], ['holiday', 'Public Holiday']];
const STAT = [['pension_employee_rate', 'Pension — Employee (%)'], ['pension_employer_rate', 'Pension — Employer (%)'], ['nhf_rate', 'NHF (%)'],
  ['nsitf_rate', 'NSITF (%)'], ['itf_rate', 'ITF (%)']];

export default function AttendanceSettings() {
  const { notify, isSuperAdmin } = useHR();
  const [policy, setPolicy] = useState(null);
  const [stat, setStat] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    Promise.all([hr.getAttendancePolicy(), hr.getStatutoryRates()]).then(([p, s]) => { setPolicy(p); setStat(s); }).catch(e => setError(e.message));
  }, []);
  if (error) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!policy || !stat) return <Loader title="Loading settings…" subtitle="Fetching parameters and rates" />;
  const edit = isSuperAdmin;
  const setP = (k, v) => setPolicy(p => ({ ...p, [k]: v }));
  const num = v => (v === '' || v === null ? null : Number(v));

  const savePolicy = async e => {
    e.preventDefault();
    setBusy('policy');
    try {
      const body = {};
      for (const [, , fields] of PARAMS) for (const [k, , t] of fields) body[k] = t === 'time' || t === 'bool' ? policy[k] : num(policy[k]);
      body.overtime_rates = Object.fromEntries(OT.map(([k]) => [k, num(policy.overtime_rates[k])]));
      setPolicy(await hr.saveAttendancePolicy(body));
      notify('System parameters and OT rates saved');
    } catch (err) { notify(err.message, 'bad'); }
    setBusy('');
  };
  const saveStat = async e => {
    e.preventDefault();
    setBusy('stat');
    try {
      const body = { ...Object.fromEntries(STAT.map(([k]) => [k, num(stat[k])])),
        paye_bands: stat.paye_bands.map(b => ({ lower_limit: num(b.lower_limit) || 0, upper_limit: num(b.upper_limit), rate: num(b.rate) || 0 })) };
      setStat(await hr.saveStatutoryRates(body));
      notify('Statutory rates and PAYE table saved');
    } catch (err) { notify(err.message, 'bad'); }
    setBusy('');
  };
  const band = (i, k, v) => setStat(s => ({ ...s, paye_bands: s.paye_bands.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const addBand = () => setStat(s => {
    const bands = [...s.paye_bands];
    const last = bands[bands.length - 1];
    if (last && last.upper_limit === null) last.upper_limit = (Number(last.lower_limit) || 0) + 1000000;
    bands.push({ lower_limit: last ? last.upper_limit : 0, upper_limit: null, rate: 0 });
    return { ...s, paye_bands: bands };
  });
  const dropBand = i => setStat(s => ({ ...s, paye_bands: s.paye_bands.filter((_, j) => j !== i) }));

  const input = (k, label, t) => (
    <label key={k} className="field"><span className="field-label">{label}</span>
      {t === 'bool'
        ? <Select value={policy[k] ? 'yes' : 'no'} disabled={!edit} onChange={e => setP(k, e.target.value === 'yes')}><option value="yes">Yes</option><option value="no">No</option></Select>
        : <input type={t === 'time' ? 'time' : 'number'} step={t === 'int' ? 1 : 'any'} value={policy[k] ?? ''} disabled={!edit} onChange={e => setP(k, e.target.value)} />}
    </label>
  );

  return (
    <div className="mdr">
      <div className="mdr-top">
        <div>
          <h2>Time &amp; Attendance Settings</h2>
          <p>System parameters, overtime rates, statutory rates and the PAYE table that attendance, timesheets and the payroll preview use</p>
        </div>
      </div>
      {!edit && <div className="mdr-card mdr-rule"><SlidersHorizontal size={15} /><span>Only a super admin can change these settings.</span></div>}

      <form onSubmit={savePolicy}>
        {PARAMS.map(([title, Ico, fields]) => (
          <div key={title} className="mdr-card tas-panel">
            <h3 className="tas-title"><Ico size={16} /> {title}</h3>
            <div className="form-grid">{fields.map(([k, l, t]) => input(k, l, t))}</div>
          </div>
        ))}
        <div className="mdr-card tas-panel">
          <h3 className="tas-title"><Timer size={16} /> OT Rates <small className="muted">— overtime multipliers of the hourly rate</small></h3>
          <div className="form-grid">
            {OT.map(([k, l]) => (
              <label key={k} className="field"><span className="field-label">{l} (×)</span>
                <input type="number" step="any" min="0" value={policy.overtime_rates[k] ?? ''} disabled={!edit}
                       onChange={e => setPolicy(p => ({ ...p, overtime_rates: { ...p.overtime_rates, [k]: e.target.value } }))} /></label>
            ))}
          </div>
        </div>
        {edit && <div className="tas-actions"><button className="mds-run" disabled={!!busy}><Save size={14} /> {busy === 'policy' ? 'Saving…' : 'Save Parameters'}</button></div>}
      </form>

      <form onSubmit={saveStat}>
        <div className="mdr-card tas-panel">
          <h3 className="tas-title"><Landmark size={16} /> Statutory Rates</h3>
          <div className="form-grid">
            {STAT.map(([k, l]) => (
              <label key={k} className="field"><span className="field-label">{l}</span>
                <input type="number" step="any" min="0" value={stat[k] ?? ''} disabled={!edit} onChange={e => setStat(s => ({ ...s, [k]: e.target.value }))} /></label>
            ))}
          </div>
        </div>
        <div className="mdr-card tas-panel">
          <h3 className="tas-title"><Calculator size={16} /> PAYE Table <small className="muted">— graduated annual tax bands</small></h3>
          <table className="mdr-table">
            <thead><tr><th>#</th><th>From (₦ / year)</th><th>To (₦ / year)</th><th>Rate (%)</th><th>Band</th>{edit && <th />}</tr></thead>
            <tbody>
              {stat.paye_bands.map((b, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><input type="number" min="0" value={b.lower_limit ?? ''} disabled={!edit || i === 0} onChange={e => band(i, 'lower_limit', e.target.value)} /></td>
                  <td><input type="number" min="0" placeholder="no limit" value={b.upper_limit ?? ''} disabled={!edit}
                             onChange={e => band(i, 'upper_limit', e.target.value === '' ? null : e.target.value)} /></td>
                  <td><input type="number" step="any" min="0" max="100" value={b.rate ?? ''} disabled={!edit} onChange={e => band(i, 'rate', e.target.value)} /></td>
                  <td className="muted">{fmtMoney(b.lower_limit || 0)} – {b.upper_limit === null || b.upper_limit === '' ? 'above' : fmtMoney(b.upper_limit)}</td>
                  {edit && <td>{stat.paye_bands.length > 1 && <button type="button" className="mdr-act a-red" title="Remove band" onClick={() => dropBand(i)}><Trash2 size={12} /></button>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {edit && <button type="button" className="mdr-btn tas-add" onClick={addBand}><Plus size={13} /> Add Band</button>}
          <p className="muted small">Each band starts where the one before ends; the first starts at 0 and the last has no upper limit.</p>
        </div>
        {edit && <div className="tas-actions"><button className="mds-run" disabled={!!busy}><Save size={14} /> {busy === 'stat' ? 'Saving…' : 'Save Statutory Rates'}</button></div>}
      </form>

      <div className="mdr-card tas-panel">
        <h3 className="tas-title"><MapPin size={16} /> Project Sites</h3>
        <p className="muted small">Sites, their outstation flag and daily outstation allowance, site transport (TP) and the other names a site has on
          pasted attendance reports are kept with the organisation structure:
          {' '}<Link to="/admin/user-access">User Access Control → Org Hierarchy → Sites</Link>.</p>
      </div>
      <HRDepartment edit={edit} notify={notify} />
    </div>
  );
}

// the HR department: its staff raise site sheets (and, later, HR requests and expenses); its head is their first approver
function HRDepartment({ edit, notify }) {
  const [s, setS] = useState(null);
  const [dept, setDept] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { hr.getHRSettings().then(x => { setS(x); setDept(x.hr_department_id || ''); }).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try { const x = await hr.saveHRSettings({ hr_department_id: dept || null }); setS(x); notify('HR department saved'); }
    catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  return (
    <div className="mdr-card tas-panel">
      <h3 className="tas-title"><Users size={16} /> HR Department</h3>
      <p className="muted small">Its staff submit the monthly site sheets; its department head is the first approver (then DMD / MD).
        {s.hr_department && (s.head ? ` Head: ${s.head}.` : ' It has no department head yet — assign one in Departments.')}</p>
      <div className="tas-row">
        <Select value={dept} disabled={!edit} onChange={e => setDept(e.target.value)} title="HR department">
          <option value="">— not set —</option>{s.departments.map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}</Select>
        {edit && <button className="mds-run" disabled={busy || String(dept) === String(s.hr_department_id || '')} onClick={save}><Save size={14} /> {busy ? 'Saving…' : 'Save'}</button>}
      </div>
    </div>
  );
}
